import { useState } from "react";
import Link from "next/link";
import PriceWithCurrency from "@/components/Helpers/PriceWithCurrency";
import {
  SAFETY_PROGRAM_TITLE,
  SAFETY_PROGRAM_CATEGORIES,
} from "@/constants/safetyProgram";

// Localized value picker — falls back to EN if a locale value is null/absent.
const L = (pair, locale) => (pair ? pair[locale] ?? pair.en ?? "" : "");

const ShieldIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2 4 5v6c0 5 3.4 8.3 8 11 4.6-2.7 8-6 8-11V5l-8-3Z"
      stroke="#00269A"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="m8.5 12 2.4 2.4L15.5 9.8"
      stroke="#00269A"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Warning triangle for "General warranty include" — exact vector from Figma
// (node 129:5840), amber #F59E0B outline + exclamation.
const WarningIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 22.1354 22.1354"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.69444 19.2929H18.4309C18.9543 19.2929 19.3501 19.0681 19.6185 18.6188C19.8868 18.1695 19.8969 17.7144 19.6488 17.2536L12.281 3.57031C11.7583 2.60016 10.3671 2.60016 9.84437 3.57031L2.47656 17.2536C2.22846 17.7144 2.23858 18.1695 2.50692 18.6188C2.77525 19.0681 3.17109 19.2929 3.69444 19.2929Z"
      stroke="#F59E0B"
      strokeWidth="1.38346"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.8125 8.44669L11.0607 13.7211L11.3084 8.44885C11.3116 8.37771 11.2885 8.31652 11.2391 8.26528C11.1896 8.21403 11.1293 8.18876 11.0581 8.18945C10.9882 8.19014 10.9292 8.21573 10.881 8.26625C10.8328 8.31676 10.8099 8.3769 10.8125 8.44669Z"
      stroke="#F59E0B"
      strokeWidth="1.38346"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.0678 17.1746C10.829 17.1746 10.6252 17.0902 10.4564 16.9214C10.2875 16.7526 10.2031 16.5487 10.2031 16.31C10.2031 16.0712 10.2875 15.8674 10.4564 15.6986C10.6252 15.5297 10.829 15.4453 11.0678 15.4453C11.3066 15.4453 11.5104 15.5297 11.6792 15.6986C11.848 15.8674 11.9325 16.0712 11.9325 16.31C11.9325 16.5487 11.848 16.7525 11.6792 16.9214C11.5104 17.0902 11.3066 17.1746 11.0678 17.1746Z"
      fill="#F59E0B"
    />
  </svg>
);

// Red "tag-right" marker shown before each warranty bullet in AR (RTL) only —
// exact vector from Figma node 129-4143 (stroke #D9232E).
const TagRightIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4.21995 3.1H15.6599C16.3399 3.1 17.19 3.57 17.55 4.15L21.73 10.83C22.13 11.48 22.09 12.5 21.63 13.11L16.45 20.01C16.08 20.5 15.28 20.9 14.67 20.9H4.21995C2.46995 20.9 1.40999 18.98 2.32999 17.49L5.09995 13.06C5.46995 12.47 5.46995 11.51 5.09995 10.92L2.32999 6.49C1.40999 5.02 2.47995 3.1 4.21995 3.1Z"
      stroke="#D9232E"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// One price cell: service-name column is plain text; all other columns are
// amounts shown with the currency icon (icon position flips with dir).
function PriceCell({ value, isServiceName, locale }) {
  // A cell is either a plain string (numbers/price ranges, locale-neutral) or a
  // { en, ar } object for localized text cells (service name, fee category).
  const text = value && typeof value === "object" ? L(value, locale) : value;
  if (isServiceName) return <span>{text}</span>;
  return (
    <PriceWithCurrency
      amount={text}
      iconClassName="w-auto h-[1.1cap]"
      className="gap-1"
    />
  );
}

function PriceTable({ table, comboNote, locale, hideSubHeading }) {
  // Some tables (e.g. TVs & Audio) shade a trailing group of "Combo" columns
  // with a light-navy tint to separate them from the EW columns (per Figma).
  const isCombo = (c) =>
    table.comboFromCol != null && c >= table.comboFromCol;
  return (
    <div className="mb-6">
      {table.subHeading && !hideSubHeading && (
        <h3 className="mb-2 font-medium text-[16px] leading-[22.2px] text-black">
          {L(table.subHeading, locale)}
        </h3>
      )}
      <div className="overflow-x-auto border border-[#E5E7EB] rounded-[10px]">
        {/* Per Figma 129-5858: base text 15.507px #111827; header bg #f9fafb
            text #787878; body rows alternate white / #f9fafb. */}
        <table className="w-full text-[15.5px] text-[#111827] border-collapse min-w-[640px]">
          <thead>
            <tr className="text-[#787878]">
              {table.columns.map((col, i) => (
                <th
                  key={i}
                  className={`px-4 py-3 font-normal text-center whitespace-pre-line align-middle ${
                    isCombo(i) ? "bg-[rgba(0,38,154,0.1)]" : "bg-[#F9FAFB]"
                  }`}
                >
                  {L(col, locale)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r} className={r % 2 ? "bg-[#F9FAFB]" : "bg-white"}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={`px-4 py-3 text-center ${
                      isCombo(c)
                        ? r % 2
                          ? "bg-[rgba(0,38,154,0.1)]"
                          : "bg-[rgba(0,38,154,0.05)]"
                        : ""
                    }`}
                  >
                    <PriceCell value={cell} isServiceName={c === 0} locale={locale} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Combo note repeats under each table (per Figma) */}
      {comboNote && L(comboNote, locale) && (
        <p className="mt-3 text-[12px] text-[#00269A]">{L(comboNote, locale)}</p>
      )}
    </div>
  );
}

function WarrantyInclude({ data, fees, locale }) {
  if (!data) return null;
  return (
    <div className="p-5 border rounded-lg">
      <div className="flex items-center gap-2 mb-4">
        <WarningIcon className="w-[22px] h-[22px] shrink-0" />
        {/* Heading: Poppins Medium 16px #111827 (per Figma) */}
        <h3 className="font-medium text-[16px] text-[#111827]">
          {L(data.heading, locale)}
        </h3>
      </div>
      {/* Warranty items — Poppins, 15.5px, #4B5563; title Medium(500), body Regular(400),
          line-height 26.563px (per Figma node 129-6583). */}
      <div className="flex flex-col gap-3">
        {data.items.map((item, i) => {
          const title = L(item.title, locale);
          const body = L(item.body, locale);
          if (!title && !body) return null; // skip items with no text for this locale
          return (
            <div
              key={i}
              className="flex items-start gap-[15px] text-[15.5px] leading-[26.563px] text-[#4B5563]"
            >
              {/* AR (RTL) only: red tag marker before each bullet (Figma 129-4143).
                  Flipped to point toward the text. Content unchanged. */}
              {locale === "ar" && (
                <TagRightIcon className="w-6 h-6 mt-0.5 shrink-0 -scale-x-100" />
              )}
              <div>
                {locale === "ar" ? (
                  // AR (per Figma): title + body on one continuous line.
                  <p className="whitespace-pre-line">
                    {title && <span className="font-medium">{title} </span>}
                    {body && <span className="font-normal">{body}</span>}
                  </p>
                ) : (
                  // EN (per Figma): title on its own line, body below.
                  <>
                    {title && <p className="font-medium">{title}</p>}
                    {body && (
                      <p className="font-normal whitespace-pre-line">{body}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Repair & replacement fees live inside this same box, per Figma */}
      <FeesTable data={fees} locale={locale} />
    </div>
  );
}

function FeesTable({ data, locale }) {
  if (!data) return null;
  // Per Figma: lavender-tinted table (header rgba(0,38,154,0.1)=#E6E9F5, body
  // rows alternate 0.05/0.1), rounded, no outer border.
  return (
    <div className="mt-6">
      <h4 className="mb-3 text-[14px] font-medium leading-[26.563px] text-black">
        {L(data.heading, locale)}
      </h4>
      <div className="overflow-x-auto rounded-lg w-fit max-w-full">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="bg-[rgba(0,38,154,0.1)] text-[#787878]">
              {data.columns.map((col, i) => (
                <th key={i} className="px-8 py-3 font-normal text-center align-middle">
                  {L(col, locale)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, r) => (
              <tr
                key={r}
                className={`text-[#111827] ${
                  r % 2 ? "bg-[#e6eaf5]" : "bg-[rgba(0,38,154,0.05)]"
                }`}
              >
                {row.map((cell, c) => (
                  <td key={c} className="px-8 py-3 text-center">
                    <PriceCell value={cell} isServiceName={c === 0} locale={locale} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Renders the body of one category (tables + combo note + warranty + fees +
// CTA). Shared by the desktop active panel and the mobile accordion.
function CategoryContent({ cat, locale, hideTabNameSubHeading }) {
  if (!cat) return null; // mobile accordion can collapse all (active = -1)
  return (
    <div>
      {/* No category title here — the active tab already shows it; the design
          goes straight to the price-table sub-headings. On mobile, a sub-heading
          equal to the tab name is hidden (it duplicates the accordion header). */}
      {cat.priceTables?.map((t, i) => (
        <PriceTable
          key={i}
          table={t}
          comboNote={cat.comboNote}
          hideSubHeading={
            hideTabNameSubHeading &&
            L(t.subHeading, locale) === L(cat.tab, locale)
          }
          locale={locale}
        />
      ))}
      <WarrantyInclude
        data={cat.warrantyInclude}
        fees={cat.repairFees}
        locale={locale}
      />
    </div>
  );
}

// Small solid arrow at the end of the CTA button (Figma node 129-1116);
// points toward the inline end (right in LTR, left in RTL).
const CtaArrow = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 7.76471 3.65398"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M0 0L3.88235 3.65398L7.76471 0H0Z" fill="white" />
  </svg>
);

function MoreInfoButton({ cat, locale }) {
  if (!cat) return null; // mobile accordion can collapse all (active = -1)
  const label = L(cat.moreInfoButton, locale);
  if (!label) return null;
  return (
    <Link
      href="/cms/al-manea-tawuniya-terms-and-conditions"
      className="flex items-center justify-between w-full max-w-[580px] gap-3 mt-8 px-4 py-3 md:px-6 md:py-4 rounded-[4px] md:rounded-lg bg-[#00269A] campaign:bg-campaignAccent font-normal text-white text-[10px] md:text-[20px]"
    >
      <span>{label}</span>
      <CtaArrow className="w-[10px] h-[6px] shrink-0 -rotate-90 rtl:rotate-90" />
    </Link>
  );
}

export default function SafetyProgram({ locale = "en" }) {
  const isAR = locale === "ar";
  const [active, setActive] = useState(0);
  const activeCat = SAFETY_PROGRAM_CATEGORIES[active];

  return (
    <div
      dir={isAR ? "rtl" : "ltr"}
      className="w-[90%] max-w-[1280px] mx-auto my-8 font-poppins"
    >
      <div className="flex items-center gap-2 mb-6">
        <ShieldIcon className="w-8 h-8 shrink-0" />
        <h1 className="text-xl font-semibold md:text-2xl">
          {L(SAFETY_PROGRAM_TITLE, locale)}
        </h1>
      </div>

      {/* Desktop: tabs + active panel */}
      <div className="hidden md:block">
        <nav className="flex flex-wrap gap-2 pb-3 mb-6 border-b">
          {SAFETY_PROGRAM_CATEGORIES.map((c, i) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(i)}
              className={`px-4 py-2 rounded-md text-sm transition-colors ${
                i === active
                  ? "bg-[#E6E9F5] text-[#00269A] campaign:text-campaignAccent font-semibold"
                  : "text-zinc-600 hover:text-[#00269A] campaign:hover:text-campaignAccent"
              }`}
            >
              {L(c.tab, locale)}
            </button>
          ))}
        </nav>
        <CategoryContent cat={activeCat} locale={locale} />
        <MoreInfoButton cat={activeCat} locale={locale} />
      </div>

      {/* Mobile: accordion (one category per collapsible) */}
      <div className="md:hidden">
        {SAFETY_PROGRAM_CATEGORIES.map((c, i) => {
          const open = i === active;
          return (
            <div key={c.key} className="mb-2 border rounded-md">
              <button
                type="button"
                onClick={() => setActive(open ? -1 : i)}
                className={`flex items-center justify-between w-full px-4 py-3 text-start text-[12px] font-medium ${
                  open
                    ? "bg-[rgba(0,88,212,0.05)] text-[#00269A]"
                    : "text-[#0C0A09]"
                }`}
                aria-expanded={open}
              >
                <span>{L(c.tab, locale)}</span>
                <span className={open ? "rotate-180" : ""} aria-hidden="true">
                  ⌄
                </span>
              </button>
              {open && (
                <div className="px-4 pb-4">
                  <CategoryContent cat={c} locale={locale} hideTabNameSubHeading />
                </div>
              )}
            </div>
          );
        })}
        {/* CTA stays visible at the bottom of the accordion regardless of which
            tab is open (per mobile design). Label is the same for every tab. */}
        <MoreInfoButton cat={SAFETY_PROGRAM_CATEGORIES[0]} locale={locale} />
      </div>
    </div>
  );
}
